import { FC, useState } from 'react';
import { useHistory } from 'umi';
import { useIntl } from 'react-intl';
import { useEventListener } from 'ahooks';
import { Prompt } from 'react-router-dom';
import { Modal } from 'iglooform';

import './style';

interface IProps {
  /**
   * @description Modal's title text
   */
  content: string;
  /**
   * @description Modal's content text
   */
  shouldConfirm: boolean;
  /**
   * @description Open the prompt message when you need to leave the route.
   */
  title?: string;
  /**
   * @description Modal footer ok button text
   */
  okText?: string;
  /**
   * @description Modal footer cancel button text
   */
  cancelText?: string;
  /**
   * @description When the tab page and browser are closed, the system-level prompt box will be displayed.
   */
  showSystemExitTip?: boolean;
  /**
   * @description The URL that always jumps when back
   */
  backRedirectUrl?: string;
  /**
   * @description Callback of changing shouldConfirm value inside the component
   */
  setShouldConfirm: (shouldConfirm: boolean) => void;
}

const prefixCls = 'ignite-vn-leave-confirm';

const LeaveConfirm: FC<IProps> = ({
  title,
  content,
  okText,
  cancelText,
  shouldConfirm,
  showSystemExitTip = true,
  backRedirectUrl,
  setShouldConfirm,
}) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [nextStep, setNextStep] = useState<any>(null);

  title =
    title ??
    formatMessage({
      id: 'Confirm to Remove',
    });
  okText =
    okText ??
    formatMessage({
      id: 'Stay',
    });
  cancelText =
    cancelText ??
    formatMessage({
      id: 'Leave',
    });

  useEventListener(
    'beforeunload',
    (event: BeforeUnloadEvent) => {
      if (shouldConfirm && showSystemExitTip) {
        event.preventDefault();
        const msg = 'Confirm Message';
        event.returnValue = msg;
        return msg;
      }
    },
    {
      target: window,
    },
  );

  return (
    <>
      <Prompt
        when={shouldConfirm}
        message={(location, action) => {
          if (!shouldConfirm) {
            return true;
          }
          setNextStep({
            action,
            location,
          });
          setVisible(true);
          return false;
        }}
      />
      <Modal
        className={`${prefixCls}-modal`}
        title={title}
        visible={visible}
        onCancel={() => {
          setVisible(false);
        }}
        onOk={() => {
          setVisible(false);
        }}
        okText={okText}
        cancelText={cancelText}
        cancelButtonProps={{
          onClick() {
            setShouldConfirm(false);

            if (nextStep.action === 'POP') {
              setTimeout(() => {
                backRedirectUrl
                  ? history.replace(backRedirectUrl)
                  : history.go(-1);
              }, 0);
            }
            if (nextStep.action === 'PUSH') {
              setTimeout(() => {
                history.push(nextStep.location.pathname);
              }, 0);
            }
            if (nextStep.action === 'REPLACE') {
              setTimeout(() => {
                history.replace(nextStep.location.pathname);
              }, 0);
            }
          },
        }}
      >
        <div>{content}</div>
      </Modal>
    </>
  );
};

export default LeaveConfirm;
